/* eslint-disable no-nested-ternary */
import { faBullseyeArrow } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Divider, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import SVG from 'react-inlinesvg';
import { ColorPalette, TypographySettings } from '../config';
import { useOmnibar } from '../omnibar/omnibar-provider';
import FasitLogoFile from './fasit-logo.svg';
import MessageCenter from '../message-center/message-center';
import { ChatDrawer } from '../chat/chat-drawer';
import { AuthorityOpticSelectContainer } from '../../areas/citizen/authority/authority-optic/authority-optic-select-container';
import { ServiceProviderOpticSelectContainer } from '../../areas/citizen/serviceprovider/components/service-provider-optic-select-container';
import { ServiceProviderOpticUserScopedContainer } from '../../areas/service-provider-unit-calendar/service-provider-optic-user-scoped-container';
import { serviceProviderUnitCalendarAreaPathName } from '../../areas/service-provider-unit-calendar/service-provider-unit-calendar-area-pathnames';
import { serviceProviderUseOfForceAreaPathName } from '../../areas/service-provider-use-of-force/service-provider-use-of-force-area-pathnames';
import { serviceProviderUnitOverviewAreaPathName } from '../../areas/service-provider-unit-overview/service-provider-unit-overview-area-pathnames';

const AUTHORITY_OPTIC_VISIBLE_ROUTES = [
  '/citizen/authority/:citizenId',
  '/citizen/authority/:citizenId/details',
  '/citizen/authority/:citizenId/journal',
  '/citizen/authority/:citizenId/communication',
  '/citizen/authority/:citizenId/interview',
  '/citizen/authority/:citizenId/plan/view',
  '/citizen/authority/:citizenId/cvjobsearch',
  '/citizen/authority/:citizenId/citizen-availability',
  '/citizen/authority/:citizenId/medical-collaboration',
  '/citizen/authority/:citizenId/unemploymentbenefits',
  '/citizen/authority/:citizenId/integration-details',
  '/citizen/authority/:citizenId/history',
];

const SP_OPTIC_VISIBLE_ROUTES = [
  '/citizen/serviceprovider/:citizenId',
  '/citizen/serviceprovider/:citizenId/details',
  '/citizen/serviceprovider/:citizenId/diaryanddocuments',
  '/citizen/serviceprovider/:citizenId/contactinformation',
  '/citizen/serviceprovider/:citizenId/communication',
  '/citizen/serviceprovider/:citizenId/calendar',
  '/citizen/serviceprovider/:citizenId/medical-collaboration',
  '/citizen/serviceprovider/:citizenId/medicine',
  '/citizen/serviceprovider/:citizenId/traffic-light',
  '/citizen/serviceprovider/:citizenId/partial-objectives-observations',
];

const SP_UNIT_OPTIC_VISIBLE_ROUTES = [`/${serviceProviderUnitCalendarAreaPathName}`, `/${serviceProviderUseOfForceAreaPathName}`, `/${serviceProviderUnitOverviewAreaPathName}`];

export function FasitLogo() {
  return (
    <SVG
      src={FasitLogoFile}
      style={{
        height: 16,
        width: 'auto',
        fill: ColorPalette.WHITE,
      }}
      title="Fasit"
      description="Fasit logo"
      cacheRequests
    />
  );
}

const IconButtonCSS = { width: 35, height: 35, flex: 0, minWidth: 35, fontSize: 19, p: 0, ml: 0.5, display: 'block' };

function matchesRoutes(pathname: string, routes: string[]) {
  return routes.some(route => new RegExp(`^${route.replace(/:[^/]+/g, '[^/]+')}$`).test(pathname));
}

function AppHeaderOpticSelector() {
  const { pathname } = useLocation();

  const isAuthorityVisible = matchesRoutes(pathname, AUTHORITY_OPTIC_VISIBLE_ROUTES);
  const isSpVisible = matchesRoutes(pathname, SP_OPTIC_VISIBLE_ROUTES);
  const isSpUnitVisible = matchesRoutes(pathname, SP_UNIT_OPTIC_VISIBLE_ROUTES);

  if (!isAuthorityVisible && !isSpVisible && !isSpUnitVisible) return null;

  return (
    <>
      <Divider orientation="vertical" flexItem sx={{ mx: 1.5, borderColor: 'rgba(255,255,255,0.4)', alignSelf: 'stretch' }} />
      {isAuthorityVisible && <AuthorityOpticSelectContainer />}
      {isSpVisible && <ServiceProviderOpticSelectContainer />}
      {isSpUnitVisible && <ServiceProviderOpticUserScopedContainer />}
    </>
  );
}

export function AppHeader(props: React.PropsWithChildren<{ actions?: React.ReactNode }>) {
  const { children, actions } = props;
  const { toggleOmnibar } = useOmnibar();

  return (
    <Box
      sx={{
        alignContent: 'space-between',
        alignItems: 'center',
        backgroundColor: ColorPalette.DeepGalaxy,
        color: ColorPalette.WHITE,
        display: 'flex',
        fontFamily: TypographySettings.DefaultFontFamily,
        height: 50,
        px: 2,
      }}
      role="banner"
    >
      <a href="/">
        <FasitLogo />
      </a>
      <Box
        sx={{
          flex: 1,
          fontSize: TypographySettings.DefaultFontSize,
          lineHeight: 1.25,
          textAlign: 'right',
          marginRight: 3,
          justifyContent: 'flex-end',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box>{children}</Box>
        {actions}

        <AppHeaderOpticSelector />
        <MessageCenter sx={{ ...IconButtonCSS }} />
        <ChatDrawer sx={{ ...IconButtonCSS }} />
        <Tooltip title="ctrl+mellemrum" placement="left" disableInteractive>
          <Button size="large" aria-label="Åbn Omnibar" onClick={() => toggleOmnibar(true)} sx={{ ...IconButtonCSS }}>
            <FontAwesomeIcon
              icon={faBullseyeArrow}
              style={
                {
                  '--fa-primary-color': ColorPalette.HotAurora,
                  '--fa-primary-opacity': 1,
                  '--fa-secondary-color': ColorPalette.WHITE,
                  '--fa-secondary-opacity': 0.8,
                } as React.CSSProperties
              }
            />
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
}
